import { computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useSessionStorage } from '@vueuse/core';
import { currentSession } from '@/utils/auth';
import isFeatureFlagEnabled from '@/utils/featureFlags.js';
import { useEnvironment } from '@/composables/useEnvironment';

const showDevology = useSessionStorage('show-devology', false);

/**
 * Standalone access check — works outside Vue component context.
 * @param {string[]|undefined} requiredSubscriptions
 * @param {string[]|undefined} allowedRoles
 * @returns {boolean}
 */
export function hasAccess(requiredSubscriptions, allowedRoles) {
  const token = currentSession() || {};
  const roles = token['cognito:groups'] || [];
  const subs = token['auxo:subs']?.split(',');

  const hasRoleGate = Array.isArray(allowedRoles) && allowedRoles.length > 0;
  const hasSubGate =
    Array.isArray(requiredSubscriptions) && requiredSubscriptions.length > 0;

  // No gates → allow
  if (!hasRoleGate && !hasSubGate) return true;

  // Role check
  if (hasRoleGate) {
    const rolePass = roles.some(r => allowedRoles.includes(r));
    if (!rolePass) return false;
  }

  // Subscription check
  if (hasSubGate) {
    // If JWT has no auxo:subs claim at all → graceful degradation (allow)
    if (subs === undefined || subs === null) return true;
    const userSubs = Array.isArray(subs) ? subs : [];
    const subPass = userSubs.some(s => requiredSubscriptions.includes(s));
    if (!subPass) return false;
  }

  return true;
}

/**
 * Clear all pinned-dropdown-* keys from sessionStorage.
 * Exported standalone for use by useUsers.js logout.
 */
export function clearNavState() {
  const keysToRemove = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && key.startsWith('pinned-dropdown-')) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => sessionStorage.removeItem(key));
  sessionStorage.removeItem('show-devology');
  sessionStorage.removeItem('sidebar-expanded');
  showDevology.value = false;
}

function buildNavTree(routes, parentPath = '', showFlag = 'showInSidebar') {
  return routes
    .filter(route => route.meta?.[showFlag])
    .filter(route =>
      hasAccess(route.meta?.requiredSubscription, route.meta?.allowedRoles)
    )
    .filter(route => {
      if (route.meta?.featureFlag) {
        return isFeatureFlagEnabled(route.meta.featureFlag);
      }
      return true;
    })
    .filter(route => {
      if (route.meta?.nonProdOnly) {
        return !useEnvironment().isProd();
      }
      return true;
    })
    .filter(route => {
      if (route.meta?.hiddenByDefault) return showDevology.value;
      return true;
    })
    .sort((a, b) => (a.meta?.order ?? 999) - (b.meta?.order ?? 999))
    .map(route => {
      const fullPath = route.path.startsWith('/')
        ? route.path
        : `${parentPath}/${route.path}`.replace(/\/+/g, '/');

      const node = {
        label: route.meta.navLabel,
        icon: route.meta.navIcon,
        path: fullPath,
        testId: route.meta.testId,
      };

      if (route.children) {
        const children = buildNavTree(route.children, fullPath, showFlag);
        if (children.length > 0) {
          node.children = children;
        }
      }

      return node;
    });
}

// Makes a template into the real route
// '/users/:auxo_user_id/profile' + { auxo_user_id: 123 } -> '/users/123/profile'
function resolveRouteParams(routeTemplate, params = {}) {
  let resolved = routeTemplate;
  Object.keys(params).forEach(key => {
    resolved = resolved.replace(`:${key}`, params[key]);
  });
  return resolved;
}

export function useTabBarItems() {
  const route = useRoute();
  const router = useRouter();

  const tabBarItems = computed(() => {
    const matchedRoute = route.matched.find(r => r.meta?.tabBarItems);
    const items = matchedRoute?.meta?.tabBarItems || [];
    const itemsWithSub = items.map(item => {
      // Resolve then read. A plain `getRoutes().find(path === route)`
      // misses routes with param regexes like `/users/:auxo_user_id(\d+)`.
      const resolvedPath = resolveRouteParams(item.route, route.params);
      const meta = router.resolve(resolvedPath).meta || {};
      return {
        ...item,
        resolvedPath,
        requiredSubscriptions: meta.requiredSubscription,
        allowedRoles: meta.allowedRoles,
      };
    });
    const availItems = itemsWithSub
      .filter(item => hasAccess(item.requiredSubscriptions, item.allowedRoles))
      .filter(item => {
        if (item.featureFlag) return isFeatureFlagEnabled(item.featureFlag);
        return true;
      });
    if (
      itemsWithSub.some(item => item.resolvedPath === route.path) &&
      !availItems.some(item => item.resolvedPath === route.path)
    ) {
      router.replace(availItems[0]?.resolvedPath || '/');
    }
    return availItems;
  });

  return { tabBarItems };
}

export function useNavigation() {
  const router = useRouter();

  const token = computed(() => currentSession() || {});
  const userRoles = computed(() => token.value['cognito:groups'] || []);
  const userSubs = computed(() => token.value['auxo:subs'] || []);

  const SideNavItems = computed(() => {
    return buildNavTree(router.options.routes);
  });

  const FooterNavItems = computed(() => {
    return buildNavTree(router.options.routes, '', 'showInFooter');
  });

  return {
    userRoles,
    userSubs,
    hasAccess,
    SideNavItems,
    FooterNavItems,
    clearNavState,
    showDevology,
  };
}
